import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { TVideoAsset } from '@/types/global';
import {
  CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Image } from '@/components/atoms/Image/Image';
import * as Styled from '../VimeoVideo/Video.styled';
import Player from '@vimeo/player';
import React from 'react';
import { LoaderSpinner } from '../LoaderSpinner/LoaderSpinner';

gsap.registerPlugin(ScrollTrigger);

export interface IVimeoVideoProps extends TVideoAsset {
  includesControls?: boolean;
  autoplay?: 'always' | 'whenVisible' | 'never';
  videoState?: 'paused' | 'play' | 'unset';
  includesSound?: boolean;
  loop?: boolean;
  vimeoSizeLimit?: number; // in MB
  includeContainer?: boolean;
  styles?: CSSProperties;
  playsOnLoad?: boolean;
  scaleVideoToFit?: boolean;
  classNameContainer?: string;
  ref?: React.Ref<HTMLVideoElement>;
}

export const VimeoVideo = React.memo(
  ({
    includesControls = false,
    loop = true,
    vimeoId = '',
    includesSound = false,
    aspectRatio = '16 / 9',
    vimeoSizeLimit,
    title,
    scaleVideoToFit = true,
    // playsOnLoad = false,
    vimeoMetadata,
    classNameContainer
  }: IVimeoVideoProps) => {
    const refVimeoVideoContainer = useRef<HTMLDivElement>(null);
    const refVimeoVideoListenerWithMargin = useRef<HTMLDivElement>(null);
    const refControlsContainer = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const refExitFullscreenIcon = useRef<HTMLImageElement>(null);
    const refFullscreenIcon = useRef<HTMLImageElement>(null);
    const [playWithSound, setPlayWithSound] = useState(false);
    const hideControlsTimeout = useRef<NodeJS.Timeout | null>(null);
    const [isVideoLoading, setIsVideoLoading] = useState(true);
    const playerRef = useRef<Player | null>(null);

    /* 
      Initializes the Vimeo player when the component mounts 
    */
    useEffect(() => {
      if (!iframeRef.current || playerRef.current) return;

      playerRef.current = new Player(iframeRef.current, {
        autopause: false,
      });
      
      playerRef.current.on('play', () => {
        setIsVideoLoading(false);
      });
      playerRef.current.on('bufferstart', () => {});
      playerRef.current.on('bufferend', () => {
        setIsVideoLoading(false);
      });
      playerRef.current.on('playing', () => {
        setIsVideoLoading(false);
      });
    }, []);

    const isFileSizeAllowed = useMemo(() => {
      if (!vimeoSizeLimit || !vimeoMetadata) return true;
      return typeof vimeoMetadata.size === 'number' && vimeoMetadata.size <= vimeoSizeLimit;
    }, [vimeoSizeLimit, vimeoMetadata]);


    /* 
      Plays the video when it comes into view 
    */
    useLayoutEffect(() => {
      const player = playerRef.current;
      if (!player) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            player
              .play()
              .catch((err) => console.warn('Vimeo play error:', err));
          } else {
            player
              .pause()
              .catch((err) => console.warn('Vimeo pause error:', err));
          }
        },
        { root: null, threshold: 0.1 }
      );

      const el = refVimeoVideoContainer.current;
      const elWithMargin = refVimeoVideoListenerWithMargin.current;
      if (el) observer.observe(el);
      if (elWithMargin) observer.observe(elWithMargin);

      return () => {
        if (el) observer.unobserve(el);
        if (elWithMargin) observer.unobserve(elWithMargin);
        observer.disconnect();
      };
    }, []);

    /* 
      Adjusts the iframe scale to cover the parent container 
    */
    useLayoutEffect(() => {
      const elIframe = iframeRef.current;
      const elIframeParent = elIframe?.parentElement;
      if (!elIframe || !elIframeParent) return;

      const parentAspectRatio =
        elIframeParent.offsetWidth / elIframeParent.offsetHeight;
      const videoAspectRatio = (vimeoMetadata?.width && vimeoMetadata?.height) && vimeoMetadata.width / vimeoMetadata.height

      if (videoAspectRatio) {
      // Calculate uniform scale to "cover" the parent
        const scale =
          parentAspectRatio > videoAspectRatio
            ? parentAspectRatio / videoAspectRatio
            : videoAspectRatio / parentAspectRatio;

        elIframe.style.transform = `scale(${scaleVideoToFit ? (scale + 0.1) : 1})`;
        elIframe.style.transformOrigin = 'center';
      }
    }, [vimeoMetadata, isFileSizeAllowed, scaleVideoToFit]);

    /* 
      Handles fullscreen toggle 
    */
    const handleFullscreen = useCallback(() => {
      const container = refVimeoVideoContainer.current;
      if (!container) return;

      // Check if current fullscreen element is your container
      const isCurrentlyFullscreen = document.fullscreenElement === container;

      if (isCurrentlyFullscreen) {
        if (refExitFullscreenIcon.current) {
          refExitFullscreenIcon.current.style.opacity = '0';
        }
        if (refFullscreenIcon.current) {
          refFullscreenIcon.current.style.opacity = '1';
        }
        document.exitFullscreen();
      } else {
        if (refExitFullscreenIcon.current) {
          refExitFullscreenIcon.current.style.opacity = '1';
        }
        if (refFullscreenIcon.current) {
          refFullscreenIcon.current.style.opacity = '0';
        }
        container.requestFullscreen();
      }
    }, []);

    /* 
      Toggles sound on/off 
    */
    const handleSoundToggle = useCallback(() => {
      const player = playerRef.current;


      if (playWithSound) {
        if (player) {
          player.setVolume(0).catch((err) => console.warn('Vimeo volume error:', err));
        }
        setPlayWithSound(false);
      } else {
        if (player) {
          player.setVolume(1).catch((err) => console.warn('Vimeo volume error:', err));
        }
        setPlayWithSound(true);
      }
    }, [playWithSound]);

    /* 
      Handles mouse movement to show/hide controls 
    */
    const handleOnMouseMove = useCallback(() => {
      if (refControlsContainer.current) {
        refControlsContainer.current.style.opacity = '1';
      }

      if (hideControlsTimeout.current) {
        clearTimeout(hideControlsTimeout.current);
      }

      hideControlsTimeout.current = setTimeout(() => {
        if (refControlsContainer.current) {
          refControlsContainer.current.style.opacity = '0';
        }
      }, 2000);
    }, []);

    return (
      <Styled.VideoContainer
        aspectRatio={aspectRatio}
        className={classNameContainer}
        ref={refVimeoVideoContainer}
      >
        <LoaderSpinner isDisplayed={isVideoLoading}/>
        {
          <div 
            ref={refVimeoVideoListenerWithMargin}
            style={{
              position: 'absolute', 
              width: '100%', 
              height: "calc(100% + 15vh)",
              top: '-15vh',
              pointerEvents: 'none',
              left: 0
            }
          }></div>
        }
        {vimeoMetadata?.poster && (
          <Image 
            src={vimeoMetadata?.poster} 
            alt="Vimeo thumbnail" 
            fill={true} 
            // styles={{ opacity: hasBeenPlayed ? 0 : 1 }}
          />
        )}
        {/* {
          isFileSizeAllowed && ( */}
            <iframe
              ref={iframeRef}
              src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1&muted=1&loop=${loop ? '1' : '0'}&controls=${includesControls ? '1' : '0'}&title=0&background=1&byline=0&portrait=0&autopause=0`}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                border: 'none',
                zIndex: 1,
                aspectRatio: aspectRatio || 'unset',
              }}
              title={title || 'video project'}
              allow="autoplay; fullscreen"
              allowFullScreen
            />
          {/* )
        } */}
        {includesControls && (
          <Styled.ControlsContainer
            ref={refControlsContainer}
            onMouseMove={handleOnMouseMove}
          >
            <button 
              onClick={handleFullscreen} 
              className="btn-fullscreen"
            >
              <Image
                src="/img/icons/fullscreen-exit.svg"
                alt="Exit Fullscreen"
                width={24}
                height={24}
                ref={refExitFullscreenIcon}
                className="exit-fullscreen-icon"
              />
              <Image
                src="/img/icons/fullscreen.svg"
                alt="Fullscreen"
                width={24}
                height={24}
                ref={refFullscreenIcon}
                className="fullscreen-icon"
              />
            </button>
            {includesSound && (
              <button onClick={handleSoundToggle} className="btn-sound">
                <Image
                  src="/img/icons/sound.svg"
                  alt="Exit Fullscreen"
                  width={24}
                  height={24}
                  className="exit-fullscreen-icon"
                />
              </button>
            )}
          </Styled.ControlsContainer>
        )}
      </Styled.VideoContainer>
    );
  }
);

VimeoVideo.displayName = 'VimeoVideo';
